<script setup lang="ts">
  import { computed, onMounted, ref } from 'vue';
  import PosUiButton from '@/components/pos-v2/ui/PosUiButton.vue';
  import { useModal } from '@/composition/use-modal';
  import type { IMoveTarget } from '@/domain/catalog/move-targets';
  import { TCategoryDeleteItems } from '@/interfaces/ICatalogV2';
  import V2CatalogTargetList from './V2CatalogTargetList.vue';

  /**
   * «¿Eliminar la categoría "{nombre}"?» with products (plan §5.15, DESIGN
   * §5.2): move them to another category (no default target) or delete them
   * too. «Eliminar categoría» waits until the choice is complete. Resolves
   * what to do with the products, or null.
   */
  const props = defineProps<{
    /** The vue-js-modal name it was opened with. */
    dialogName: string;
    name: string;
    itemCount: number;
    targets: IMoveTarget[];
    branchName: string;
  }>();

  type TChoice = 'move' | 'delete';

  const modals = useModal();
  const root = ref<HTMLElement | null>(null);
  // No default: deleting products is always an explicit choice.
  const choice = ref<TChoice | null>(null);
  const targetId = ref<number | null>(null);

  const result = computed<TCategoryDeleteItems | null>(() => {
    if (choice.value === 'delete') return { items: 'delete' };
    if (choice.value === 'move' && targetId.value !== null)
      return { items: 'move', to: targetId.value };
    return null;
  });

  function pickTarget(categoryId: number) {
    choice.value = 'move';
    targetId.value = categoryId;
  }

  function answer(value: TCategoryDeleteItems | null) {
    // Closes and unmounts the dialog: nothing may run after this.
    modals.dialogSubmit(props.dialogName, value);
  }

  onMounted(() => {
    root.value
      ?.querySelector<HTMLElement>('.delete-cancel')
      ?.focus({ preventScroll: true });
  });
</script>

<template>
  <div
    ref="root"
    class="v2-catalog-category-delete-dialog"
    role="alertdialog"
    :aria-label="$t('menuPage.categoryDelete.title', { name })"
    @keydown.esc.stop="answer(null)"
  >
    <h2 class="delete-title">
      {{ $t('menuPage.categoryDelete.title', { name }) }}
    </h2>
    <p class="delete-question">
      {{ $tc('menuPage.categoryDelete.question', itemCount, { n: itemCount }) }}
    </p>

    <div class="delete-choices" role="radiogroup">
      <div
        v-if="targets.length"
        class="delete-choice"
        :class="{ 'is-selected': choice === 'move' }"
      >
        <button
          type="button"
          role="radio"
          class="choice-head"
          :aria-checked="choice === 'move' ? 'true' : 'false'"
          @click="choice = 'move'"
        >
          <span class="choice-radio" />
          <span class="choice-label">{{
            $tc('menuPage.categoryDelete.move', itemCount)
          }}</span>
        </button>
        <V2CatalogTargetList
          v-show="choice === 'move'"
          class="choice-targets"
          :value="targetId"
          :targets="targets"
          :branch-name="branchName"
          :label="$tc('menuPage.categoryDelete.move', itemCount)"
          @input="pickTarget"
          @pick="pickTarget"
        />
      </div>

      <div
        class="delete-choice"
        :class="{ 'is-selected': choice === 'delete' }"
      >
        <button
          type="button"
          role="radio"
          class="choice-head"
          :aria-checked="choice === 'delete' ? 'true' : 'false'"
          @click="choice = 'delete'"
        >
          <span class="choice-radio" />
          <span class="choice-label">
            <span class="label-strong">{{
              $tc('menuPage.categoryDelete.deleteToo', itemCount)
            }}</span>
            {{ $tc('menuPage.categoryDelete.deleteTooHint', itemCount) }}
          </span>
        </button>
      </div>
    </div>

    <p class="delete-footnote">{{ $t('menuPage.categoryDelete.salesKept') }}</p>

    <div class="delete-actions">
      <PosUiButton
        class="delete-cancel"
        color="neutral"
        variant="ghost"
        @click="answer(null)"
        >{{ $t('cancel') }}</PosUiButton
      >
      <PosUiButton
        class="delete-accept"
        color="error"
        variant="soft"
        :disabled="!result"
        @click="answer(result)"
        >{{ $t('menuPage.categoryDelete.accept') }}</PosUiButton
      >
    </div>
  </div>
</template>

<style scoped lang="scss">
  .v2-catalog-category-delete-dialog {
    @apply tw-flex tw-flex-col tw-bg-surface tw-px-5 tw-pb-4 tw-pt-5;

    .delete-title {
      @apply tw-m-0 tw-text-base tw-font-semibold tw-leading-snug tw-text-neutral-900;
    }

    .delete-question {
      @apply tw-mb-0 tw-mt-2 tw-text-sm tw-leading-relaxed tw-text-neutral-600;
    }

    .delete-choices {
      @apply tw-mt-3;
    }

    .delete-choice {
      @apply tw-rounded-xl tw-border tw-border-solid tw-border-neutral-200 tw-bg-surface;

      & + .delete-choice {
        @apply tw-mt-2;
      }

      &.is-selected {
        @apply tw-border-neutral-900;

        .choice-radio {
          @apply tw-border-neutral-900 tw-bg-neutral-900;
          box-shadow: inset 0 0 0 3px rgb(var(--c-surface));
        }
      }

      .choice-head {
        @apply tw-flex tw-min-h-[48px] tw-w-full tw-cursor-pointer tw-items-start tw-rounded-xl tw-border-0 tw-bg-transparent tw-px-3 tw-py-3 tw-text-left tw-text-sm tw-text-neutral-700;

        &:focus-visible {
          @apply tw-outline-none tw-ring-2 tw-ring-orange-500/40;
        }
      }

      .choice-radio {
        @apply tw-mr-3 tw-mt-px tw-h-[18px] tw-w-[18px] tw-flex-none tw-rounded-full tw-border-2 tw-border-solid tw-border-neutral-300 tw-bg-surface;
      }

      .choice-label {
        @apply tw-min-w-0 tw-flex-1 tw-leading-snug;

        .label-strong {
          @apply tw-font-semibold tw-text-neutral-900;
        }
      }

      .choice-head .choice-label {
        @apply tw-font-medium;
      }

      .choice-targets {
        @apply tw-px-3 tw-pb-3;
      }
    }

    .delete-footnote {
      @apply tw-mb-0 tw-mt-3 tw-text-xs tw-text-neutral-500;
    }

    // Margins, not flex gap (Chrome < 84).
    .delete-actions {
      @apply tw-mt-4 tw-flex tw-items-center tw-justify-end;

      .delete-accept {
        @apply tw-ml-2;
      }
    }
  }
</style>
